import { apiClient } from './apiClient';

export type WcQuotable = 'yes' | 'no' | 'pending' | 'not_set';

/** A label Core (or the team) can give: WhatConverts' "not set" is not one. */
export type WcLabel = 'yes' | 'no' | 'pending';

/** One labeler's newest label for a lead ("ai_scorer" today, "jev" later). */
export interface WcLeadLabel {
  labeler: string;
  version: string;
  label: WcLabel;
  reason: string | null;
  model: string | null;
  created_at: string | null;
}

/** The team's answer for a lead. Append-only on the server; newest wins. */
export interface WcLeadCorrection {
  label: WcLabel;
  note: string | null;
  actor_email: string;
  tenant_id: string;
  created_at: string | null;
}

export interface WcLead {
  lead_id: number | string;
  lead_type: string;
  quotable: WcQuotable;
  quote_value: number | null;
  /** WhatConverts's own flags (normalize_lead returns both). */
  spam?: boolean | null;
  duplicate?: boolean | null;
  sales_value?: number | null;
  gclid: string | null;
  lead_source: string | null;
  lead_medium: string | null;
  contact_name?: string | null;
  date_created: string | null;
  /** Present when lead labels are on: newest label per labeler. */
  labels?: WcLeadLabel[];
  /** The Core AI label (labeler "ai_scorer"), or null when not labelled yet. */
  ai_label?: WcLeadLabel | null;
  /** The team's newest correction, or null. */
  correction?: WcLeadCorrection | null;
  /** Changes to this lead made outside Core that earn the badge (a mark Core wrote that was changed, or a spam flag); 0 or absent: none. */
  outside_changes?: number;
}

export interface WcLeadLabelsMeta {
  enabled: boolean;
  available: boolean;
  labelers?: string[];
  current_version?: string;
}

export interface WcLeadsResponse {
  tenant_id: string | null;
  profile_id: string;
  period: { start: string; end: string };
  count: number;
  leads: WcLead[];
  /** "not_recognised": WhatConverts no longer knows the mapped profile. */
  profile_status?: 'ok' | 'not_recognised';
  lead_labels?: WcLeadLabelsMeta;
}

// Read normalized WhatConverts leads. Provide profileId (direct) or tenantId
// (resolves the mapping). Backend returns stub data until creds are set.
export async function getWcLeads(params: {
  tenantId?: string;
  profileId?: string;
  days?: number;
  quotable?: WcQuotable;
}): Promise<WcLeadsResponse> {
  const { data } = await apiClient.get<WcLeadsResponse>('/whatconverts/leads', {
    params: {
      tenant_id: params.tenantId,
      profile_id: params.profileId,
      days: params.days ?? 90,
      quotable: params.quotable,
    },
  });
  return data;
}

export async function postLeadCorrection(params: {
  tenantId: string;
  wcLeadId: string | number;
  label: WcLabel;
  note?: string;
  dateCreated?: string | null;
}): Promise<{ wc_lead_id: string; correction: WcLeadCorrection; history: WcLeadCorrection[] }> {
  const { data } = await apiClient.post('/whatconverts/lead-labels/corrections', {
    tenant_id: params.tenantId,
    wc_lead_id: String(params.wcLeadId),
    label: params.label,
    note: params.note || undefined,
    date_created: params.dateCreated ?? undefined,
  });
  return data;
}

/** What Core did about writing this lead's quotable mark to WhatConverts. */
export interface WcLeadWriteAttempt {
  status: 'pending' | 'success' | 'error' | 'skipped' | 'dry_run';
  reason: string | null;
  before_quotable: WcQuotable | null;
  after_quotable: WcLabel | null;
  trigger: string;
  labeler: string | null;
  created_at: string | null;
  finished_at: string | null;
}

/**
 * Who owns the client's quotable mark. effective: "planner" (Core writes
 * nothing), "core_dry_run" (Core records what it would write) or
 * "core_live" (Core writes). Null when the client has no write-back setup.
 */
export interface WcLeadWriteback {
  owner: 'planner' | 'core';
  effective: 'planner' | 'core_dry_run' | 'core_live';
  labeler: string;
  latest: WcLeadWriteAttempt | null;
}

/** A change made to a lead outside Core, seen at an import or re-read. */
export interface WcOutsideChange {
  field: 'quotable' | 'spam' | 'quote_value' | 'sales_value' | string;
  old_value: string | null;
  new_value: string | null;
  core_last_written: string | null;
  seen_at: string;
  source: 'import' | 'reread' | string;
  /** WhatConverts does not say who; null unless a source ever does. */
  changed_by: string | null;
  /** When WhatConverts says the lead changed (its last_updated), if it sent one. */
  wc_last_updated?: string | null;
  /** true: WhatConverts sent no timezone, so wc_last_updated is not a reliable instant. */
  wc_last_updated_naive?: boolean | null;
}

export interface WcLeadLabelDetail {
  wc_lead_id: string;
  labels: WcLeadLabel[];
  corrections: WcLeadCorrection[];
  current_version: string;
  writeback?: WcLeadWriteback | null;
  outside_changes?: WcOutsideChange[];
}

export async function getLeadLabelDetail(
  tenantId: string,
  wcLeadId: string | number,
): Promise<WcLeadLabelDetail> {
  const { data } = await apiClient.get<WcLeadLabelDetail>(
    `/whatconverts/lead-labels/leads/${encodeURIComponent(String(wcLeadId))}`,
    { params: { tenant_id: tenantId } },
  );
  return data;
}

export interface WcLabelerStats {
  labelled: number;
  compared: number;
  agree: number;
  disagree: number;
  wc_not_set: number;
  agreement_rate: number | null;
  matrix: Record<WcLabel, Record<WcLabel, number>>;
  vs_team: { corrected: number; right: number; accuracy: number | null };
}

export interface WcLeadLabelStats {
  tenant_id: string | null;
  days: number;
  leads: number;
  wc_marked: number;
  corrected: number;
  whatconverts_vs_team: { corrected: number; right: number; accuracy: number | null };
  labelers: Record<string, WcLabelerStats>;
}

export async function getLeadLabelStats(tenantId: string, days: number): Promise<WcLeadLabelStats> {
  const { data } = await apiClient.get<WcLeadLabelStats>('/whatconverts/lead-labels/stats', {
    params: { tenant_id: tenantId, days },
  });
  return data;
}

/** One lead waiting on a Yes/No, or one missed call / voicemail without follow-up. */
export interface WcFollowUpLead {
  lead_id: string;
  profile_id: string | null;
  date_created: string | null;
  age_days: number | null;
  severity: 'ok' | 'amber' | 'red';
  lead_type: string | null;
  contact_name: string | null;
  wc_quotable: WcQuotable | null;
  reason: string | null;
  labeler: string | null;
  qualification: string | null;
  /** pending queue: what put it there ("label" or "wc_mark"). */
  source: string;
  /** missed calls: it was a voicemail with a request. */
  voicemail?: boolean;
}

export interface WcPendingClient {
  client: string;
  profile_id: string | null;
  total: number;
  ok: number;
  amber: number;
  red: number;
  oldest_age_days: number | null;
}

export interface WcPendingQueue {
  tenant_id: string | null;
  summary: {
    total: number;
    ok: number;
    amber: number;
    red: number;
    oldest_age_days: number | null;
    /** The read hit its limit; the newest leads are not listed. */
    truncated?: boolean;
  };
  by_client: WcPendingClient[];
  leads: WcFollowUpLead[];
  thresholds: { amber_days: number; red_days: number };
}

export interface WcMissedCalls {
  tenant_id: string | null;
  summary: { count: number; clients: number; oldest_age_days: number | null; truncated?: boolean };
  by_client: { client: string; profile_id: string | null; count: number; oldest_age_days: number | null }[];
  leads: WcFollowUpLead[];
  definition: { min_age_days: number; text: string };
}

export async function getPendingQueue(tenantId: string): Promise<WcPendingQueue> {
  const { data } = await apiClient.get<WcPendingQueue>('/whatconverts/lead-labels/pending', {
    params: { tenant_id: tenantId },
  });
  return data;
}

export async function getMissedCalls(tenantId: string): Promise<WcMissedCalls> {
  const { data } = await apiClient.get<WcMissedCalls>('/whatconverts/lead-labels/missed-calls', {
    params: { tenant_id: tenantId },
  });
  return data;
}
